import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { showToast } from '../utils/helpers';
import { 
  loadCart, 
  saveCart, 
  setupCartSync, 
  clearCart as clearCartStorage 
} from '../utils/cart';
import api from '../utils/api';
import { API_CONFIG } from '../utils/apiConfig';

import StoreFrontFooter from '../components/store/footer';
import { useMeoContext } from '../contexts/MeoContext';
import { useAuth } from '../contexts/AuthContext';
import { useAddressBook } from '../contexts/AddressBookContext';
import ContactStoreModal from '../components/modals/ContactStoreModal';
import Header from '../components/store/header';
import CartModal from '../components/modals/CartModal';
import ShareStoreModal from '../components/modals/ShareProductModal';
import ServiceBookingModal from '../components/modals/ServiceBookingModal';
import { initNegotiation, acceptNegotiation, updateNegotiationContext, getActiveSession, logConversation, getNegotiation } from '../services/negotiationService';
import { saveNegotiationSession, getNegotiationByProductSlug, removeNegotiationSession } from '../utils/negotiationStorage';
import { resolvePersonalLogin } from '../utils/personalLogin';
import { Shield, RefreshCw, Truck, Check, Star, ShoppingBag, ChevronLeft, ChevronRight, Maximize, X } from 'lucide-react';

// Constants for better maintainability
const TABS = {
  DESCRIPTION: 'description',
  SPECIFICATIONS: 'specifications',
  REVIEWS: 'reviews',
  DELIVERY: 'delivery'
};

const LISTING_TYPES = {
  PRODUCT: 'product',
  SERVICE: 'service'
};

const TRUST_BADGES = [
  {
    icon: <Shield className="w-6 h-6 text-blue-600" />,
    label: 'Secure Payment'
  },
  {
    icon: <RefreshCw className="w-6 h-6 text-blue-600" />,
    label: 'Free Returns'
  },
  {
    icon: <Truck className="w-6 h-6 text-blue-600" />,
    label: 'Fast Delivery'
  }
];

// Debug component to check meta tags
const MetaTagDebugger = () => {
  useEffect(() => {
    // console.log('=== PRODUCT PAGE META TAGS DEBUG ===');
    // console.log('Document Title:', document.title);

    const metaTags = document.querySelectorAll('meta');
    metaTags.forEach(tag => {
      const name = tag.getAttribute('name') || tag.getAttribute('property');
      const content = tag.getAttribute('content');
      if (name && (name.includes('og:') || name.includes('twitter:') || name === 'description')) {
        // console.log(`${name}:`, content);
      }
    });

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      // console.log('Canonical URL:', canonical.getAttribute('href'));
    }

    const structuredData = document.querySelectorAll('script[type="application/ld+json"]');
    structuredData.forEach((script, index) => {
      try {
        const data = JSON.parse(script.textContent);
        if (data['@type'] === 'Product') {
          // console.log('Product Structured Data:', data);
        }
      } catch (error) {
        // console.log(`Structured Data ${index + 1}: Parse error`);
      }
    });
  }, []);

  return null;
};

const SharedProductPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [companyData, setCompany] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showCartModal, setShowCartModal] = useState(false);
  const [showServiceBookingModal, setShowServiceBookingModal] = useState(false);
  const [cart, setCart] = useState([]);
  const [showShareStoreModal, setShowShareStoreModal] = useState(false);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState(TABS.DESCRIPTION);
  const [showImageModal, setShowImageModal] = useState(false);

  // Pagination states
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedPagination, setRelatedPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalProducts: 0,
    hasNext: false,
    hasPrev: false,
    limit: 12
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [allRelatedProductsLoaded, setAllRelatedProductsLoaded] = useState(false);
  const [agreedPrice, setAgreedPrice] = useState(null);
  const [isNegotiating, setIsNegotiating] = useState(false);
  const negotiationSessionIdRef = useRef(null);
  const existingSessionRef = useRef(null);
  const cartInitializedRef = useRef(false);
  const { setOnPriceAgreed, setOnNegotiationStart, setShowMeo } = useMeoContext();
  const { currentUser } = useAuth();
  const { refreshAddresses } = useAddressBook();

  const currentUserId = currentUser?.company_id || sessionStorage.getItem('negotiationGuestId') || null;

  // The cart is scoped to the store this shared product belongs to
  const storeId = companyData?.username || companyData?.company_id || null;

  // Show Meo agent on product pages
  useEffect(() => {
    setShowMeo(true);
    return () => setShowMeo(false);
  }, [setShowMeo]);

  // SSO: a token attached to the product link (from the paymeo-webapp
  // marketplace) logs the personal user into the cart without re-entering
  // their details. Stripped from the URL once consumed.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      if (!token) return;

      const url = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, url);

      if (token) {
        resolvePersonalLogin(token).then(() => refreshAddresses());
      }
    } catch (error) {
      console.error('Personal SSO token handling failed:', error);
    }
  }, [refreshAddresses]);


  // On product load, check for and restore existing active negotiation sessions
  useEffect(() => {
    if (!product || !companyData) return;
    const restoreSession = async () => {
      const productId = product.id || product.product_id;
      const productSlug = product.slug;
      const userId = currentUserId;
      let restoredSession = null;
      let restoredLogs = null;

      // 1. Try sessionStorage for this product's slug
      if (productSlug) {
        const stored = getNegotiationByProductSlug(productSlug);
        if (stored?.negotiation_id) {
          const result = await getNegotiation(stored.negotiation_id);
          if (result.status === 'success') {
            const session = result.session || result.negotiation;
            if (session) {
              restoredSession = session;
              restoredLogs = result.logs || [];
            } else {
              removeNegotiationSession(productSlug);
            }
          } else {
            removeNegotiationSession(productSlug);
          }
        }
      }

      // 2. Fallback: lookup by user+product
      if (!restoredSession && userId) {
        const result = await getActiveSession(productId, userId);
        if (result.status === 'success' && result.session) {
          restoredSession = result.session;
          restoredLogs = result.logs || [];
        }
      }

      // 3. Apply restored session data
      if (restoredSession) {
        existingSessionRef.current = restoredSession;
        negotiationSessionIdRef.current = restoredSession.session_id;
        if (restoredSession.agreed_amount) {
          setAgreedPrice(Number(restoredSession.agreed_amount));
        }
        if (restoredSession.negotiation_status === 'active') {
          setIsNegotiating(true);
        }
        window.__NEGOTIATION_CONTEXT__ = {
          session_id: restoredSession.session_id,
          agreed_amount: restoredSession.agreed_amount,
          original_amount: restoredSession.original_amount,
          negotiation_status: restoredSession.negotiation_status,
          logs: restoredLogs
        };
        // Persist to sessionStorage for future visits
        if (productSlug) {
          saveNegotiationSession({
            user_id: userId,
            negotiation_id: restoredSession.session_id,
            product_slug: productSlug,
            negotiation_status: restoredSession.negotiation_status
          });
        }
      }
    };
    restoreSession();
  }, [product, companyData, currentUserId]);

  // Register page-specific callbacks with shared Meo agent
  useEffect(() => {
    const handleNegotiationStart = async () => {
      setIsNegotiating(true);
      if (!product || !companyData) return;

      // If we already have an active session, reuse it
      if (negotiationSessionIdRef.current) {
        const existingId = negotiationSessionIdRef.current;
        // Refresh the expiry by updating context
        await updateNegotiationContext(existingId, {
          final_context: 'Negotiation resumed via Meo AI agent',
          product_id: product.id || product.product_id
        });
        await logConversation(existingId, {
          action: 'system_note',
          message: 'Negotiation resumed by user',
          actor: 'system'
        });
        return;
      }

      const result = await initNegotiation({
        company_id: companyData.company_id || companyData.id,
        product_id: product.id || product.product_id,
        user_id: currentUserId,
        original_amount: product.per_unit_cost,
        starting_price: product.starting_price,
        ending_price: product.ending_price,
        initial_context: 'Negotiation started via Meo AI agent'
      });
      if (result.status === 'success') {
        negotiationSessionIdRef.current = result.session_id;
        // Store guest ID for session resumption
        if (result.user_id && !currentUser?.company_id) {
          sessionStorage.setItem('negotiationGuestId', result.user_id);
        }
        // Persist negotiation session to sessionStorage
        if (product?.slug) {
          saveNegotiationSession({
            user_id: result.user_id || currentUserId,
            negotiation_id: result.session_id,
            product_slug: product.slug,
            negotiation_status: 'active'
          });
        }
      }
    };

    const handlePriceAgreed = async (price) => {
      setAgreedPrice(price);
      if (negotiationSessionIdRef.current) {
        await acceptNegotiation(negotiationSessionIdRef.current, {
          agreed_amount: price,
          actor: 'buyer'
        });
        await logConversation(negotiationSessionIdRef.current, {
          action: 'price_accepted',
          message: `Buyer accepted price at ₦${Number(price).toLocaleString()}`,
          new_amount: price,
          actor: 'buyer'
        });
        // Update sessionStorage negotiation status to accepted
        if (product?.slug) {
          saveNegotiationSession({
            user_id: currentUserId,
            negotiation_id: negotiationSessionIdRef.current,
            product_slug: product.slug,
            negotiation_status: 'accepted'
          });
        }
      }
    };

    setOnPriceAgreed(handlePriceAgreed);
    setOnNegotiationStart(handleNegotiationStart);
    return () => {
      setOnPriceAgreed(null);
      setOnNegotiationStart(null);
      negotiationSessionIdRef.current = null;
      existingSessionRef.current = null;
      window.__NEGOTIATION_CONTEXT__ = null;
    };
  }, [setOnPriceAgreed, setOnNegotiationStart, product, companyData, currentUserId]);

  const baseUrl = window.location.origin;
  const shareLink = `${baseUrl}/product/${productId}`;

  // Helper functions - defined first to avoid initialization errors
  const getListingTypeDisplay = useCallback((product) => {
    const uploadType = product.upload_type || LISTING_TYPES.PRODUCT;
    return uploadType === LISTING_TYPES.SERVICE ? 'Service' : 'Product';
  }, []);

  // Helper to check if product is digital
  const isDigitalProduct = useCallback((product) => {
    return product?.is_digital === true || product?.is_digital === 'true' || product?.is_digital === 1;
  }, []);

  // Get product type badge with digital support
  const getProductTypeBadge = useCallback((product) => {
    if (isDigitalProduct(product)) {
      return (
        <span className="inline-flex px-2 py-1 text-xs font-bold bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 rounded-full">
          Digital Product
        </span>
      );
    }
    
    const listingType = getListingTypeDisplay(product);
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${
        listingType === 'Service' 
          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' 
          : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      }`}>
        {listingType}
      </span>
    );
  }, [isDigitalProduct, getListingTypeDisplay]);

  // Helper functions
  const getProductImages = useCallback((product) => {
    if (!product) return [];
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images.map(img => img.image_url || img.url || img);
    }
    if (product.product_image) {
      const images = product.product_image.split(',');
      return images.map(url => url.trim()).filter(url => url);
    }
    return [];
  }, []);

  // Enhanced delivery options helper - skip for digital products
  const getDeliveryOptions = useCallback((product) => {
    if (isDigitalProduct(product)) return null;
    
    if (!product?.delivery_options) return null;
    
    try {
      if (typeof product.delivery_options === 'string') {
        return JSON.parse(product.delivery_options);
      }
      return product.delivery_options;
    } catch (error) {
      console.error('Error parsing delivery options:', error);
      return null;
    }
  }, [isDigitalProduct]);

  const hasNegotiablePricing = useCallback((product) => {
    return !!product?.is_negotiable;
  }, []);

  const getPriceDisplay = useCallback((product) => {
    const basePrice = product?.per_unit_cost || 0;
    return `₦${parseFloat(basePrice).toLocaleString()}`;
  }, []);

  // Get delivery summary - skip for digital products
  const getDeliverySummary = useCallback((product) => {
    if (isDigitalProduct(product)) return 'Instant Digital Download';
    
    const deliveryOptions = getDeliveryOptions(product);
    if (!deliveryOptions) return 'No delivery options';
    const options = [];
    if (deliveryOptions.pickup?.available) {
      options.push('Pickup');
    }
    if (deliveryOptions.delivery?.available) {
      const fees = deliveryOptions.delivery.fees;
      if (fees?.lagos || fees?.outside_lagos) {
        options.push('Delivery');
      }
    }
    return options.length > 0 ? options.join(' • ') : 'No delivery options';
  }, [getDeliveryOptions, isDigitalProduct]);

  const getConditionLabel = useCallback((condition) => {
    const conditions = {
      'new': 'New',
      'used': 'Used',
      'refurbished': 'Refurbished'
    };
    return conditions[condition] || condition || 'Not specified';
  }, []);

  const getDeliveryTimelineLabel = useCallback((timeline) => {
    const timelines = {
      '1': '1 day',
      '2': '2 days',
      '3': '3 days',
      '7': '1 week',
      '14': '14 days',
      '21': '21 days'
    };
    return timelines[timeline] || (timeline ? `${timeline} days` : 'Not specified');
  }, []);

  const formatCurrency = useCallback((amount, currency = 'NGN') => {
    if (!amount) return currency === 'NGN' ? '₦0' : '$0';
    try {
      const options = {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      };
      return new Intl.NumberFormat(currency === 'NGN' ? 'en-NG' : 'en-US', options).format(amount);
    } catch (error) {
      return currency === 'NGN' ? `₦${amount}` : `$${amount}`;
    }
  }, []);

  // Enhanced inventory check for digital products and services
// Enhanced inventory check for grouped variations
const getAvailableStock = useCallback((product, selectedVariations = {}) => {
  if (!product) return 0;
  
  // For digital products, always return 1 (available)
  if (isDigitalProduct(product)) {
    return 1;
  }
  
  // For services, always return 1 (available) since we don't check inventory
  if (getListingTypeDisplay(product) === 'Service') {
    return 1;
  }
  const totalInventory = product.inventory || Object.values(selectedVariations).map(v => v.inventory || 0).reduce((a, b) => a + b, 0);
  
  // If no variations, use product inventory
  if (!product.variations || Object.keys(product.variations).length === 0) {
    return (product.inventory || 0);
  }
  
  // If variations exist but none selected, return 0 (user must select variations)
  if (Object.keys(selectedVariations).length === 0) {
    return product.inventory || totalInventory || 0;
  }
  // console.log("SELECTED VARIATIONS", selectedVariations);
  
  // Find the specific variation combination
  // For simplicity, we'll use the first variation's inventory
  // In a real app, you'd want to track inventory for specific combinations
  // const firstVariationType = Object.keys(selectedVariations)[0];
  // const firstVariation = selectedVariations[firstVariationType];
  return totalInventory;
}, [getListingTypeDisplay, isDigitalProduct]);

// Enhanced function to calculate final price with variations
const getFinalPrice = useCallback((product, selectedVariations = {}, overridePrice = null) => {
  if (overridePrice !== null) return Number(overridePrice);
  
  const basePrice = product?.per_unit_cost || 0;
  
  if (hasNegotiablePricing(product)) {
    return basePrice; // Negotiable pricing doesn't use variations
  }
  
  let totalAdjustment = 0;
  
  // Add up all price adjustments from selected variations
  Object.values(selectedVariations).forEach(variation => {
    if (variation && variation.price_adjustment) {
      totalAdjustment += variation.price_adjustment;
    }
  });
  
  return basePrice + totalAdjustment;
}, [hasNegotiablePricing]);

// Update the addToCart function to handle grouped variations
const addToCart = useCallback((product, selectedVariations = {}) => {
  // For services, show the booking modal instead of adding to cart
  if (getListingTypeDisplay(product) === 'Service') {
    setShowServiceBookingModal(true);
    return;
  }

  const productImages = getProductImages(product);
  const primaryImage = productImages.length > 0 ? productImages[0] : null;
  const finalPrice = getFinalPrice(product, selectedVariations, agreedPrice);

  const cartItem = {
    ...product,
    quantity,
    variations: selectedVariations,
    finalPrice: finalPrice,
    product_image: primaryImage,
    images: productImages,
    is_digital: isDigitalProduct(product),
    addedAt: new Date().toISOString(),
    negotiation_session_id: negotiationSessionIdRef.current,
    agreed_price: agreedPrice
  };

  setCart(prevCart => {
    const existingItemIndex = prevCart.findIndex(item =>
      item.id === product.id &&
      JSON.stringify(item.variations) === JSON.stringify(selectedVariations)
    );

    let updatedCart;
    
    if (existingItemIndex > -1) {
      updatedCart = [...prevCart];
      updatedCart[existingItemIndex] = {
        ...updatedCart[existingItemIndex],
        quantity: updatedCart[existingItemIndex].quantity + quantity,
        updatedAt: new Date().toISOString()
      };
    } else {
      updatedCart = [...prevCart, cartItem];
    }

    return updatedCart;
  });

  showToast('Added to cart!', 'success');
  setQuantity(1);
}, [quantity, getProductImages, getListingTypeDisplay, isDigitalProduct, storeId, getFinalPrice, agreedPrice]);

  // Delivery fee helpers
  const lagosDeliveryFee = useCallback((fee, currency) => {
    if (fee && fee > 0) {
      return (
        <div className="flex justify-between items-center text-sm">
          <span className="text-blue-700 dark:text-blue-400">Lagos Delivery Fee:</span>
          <span className="font-bold text-blue-900 dark:text-blue-200">
            {currency} {parseFloat(fee).toLocaleString()}
          </span>
        </div>
      );
    }
    return null;
  }, []);

  const outsideLagosDelivery = useCallback((fee, currency) => {
    if (fee && fee > 0) {
      return (
        <div className="flex justify-between items-center text-sm">
          <span className="text-blue-700 dark:text-blue-400">Outside Lagos Delivery Fee:</span>
          <span className="font-bold text-blue-900 dark:text-blue-200">
            {currency} {parseFloat(fee).toLocaleString()}
          </span>
        </div>
      );
    }
    return null;
  }, []);

  // Helper function to get product image for related products
  const getOtherProductImage = useCallback((product) => {
    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
      return product.images[0].image_url || product.images[0].url || product.images[0];
    }
    if (product.product_image) {
      const images = product.product_image.split(',');
      return images[0].trim();
    }
    return null;
  }, []);

  // Enhanced cart synchronization (per-store)
  useEffect(() => {
    if (!storeId) return;

    // Setup cross-tab synchronization
    const cleanup = setupCartSync(storeId, () => {
      // Reload cart when updated in another tab
      loadCart(storeId).then(storeCart => setCart(storeCart));
    });

    return cleanup;
  }, [storeId]);

  // Enhanced cart initialization
  useEffect(() => {
    if (!storeId) return;

    const initializeCart = async () => {
      try {
        const storeCart = await loadCart(storeId);
        console.log('SharedProductPage: Initial cart load:', storeCart.length, 'items');
        cartInitializedRef.current = true;
        setCart(storeCart);
      } catch (error) {
        console.error('SharedProductPage: Error initializing cart:', error);
        cartInitializedRef.current = true;
        setCart([]);
      }
    };

    initializeCart();
  }, [storeId]);

  // Enhanced cart persistence with debouncing
  useEffect(() => {
    if (!storeId || !cartInitializedRef.current) return;

    const saveCartData = async () => {
      try {
        await saveCart(cart, storeId);
        console.log('SharedProductPage: Cart saved successfully');
      } catch (error) {
        console.error('SharedProductPage: Error saving cart:', error);
      }
    };

    // Debounce cart saves to prevent excessive writes
    const timeoutId = setTimeout(saveCartData, 500);
    return () => clearTimeout(timeoutId);
  }, [cart, storeId]);

  const cartItemCount = useMemo(() =>
    cart.reduce((count, item) => count + item.quantity, 0),
    [cart]
  );

  const cartTotal = useMemo(() =>
    cart.reduce((total, item) => total + ((item.finalPrice || item.per_unit_cost) * item.quantity), 0),
    [cart]
  );


  // Enhanced removeFromCart with immediate persistence
  const removeFromCart = useCallback((productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
    showToast('Product removed from cart', 'info');
  }, []);

  // Enhanced clearCart with persistence
  const clearCartLocal = useCallback(() => {
    setCart([]);
    clearCartStorage(storeId);
    showToast('Cart cleared', 'info');
  }, [storeId]);

  // Enhanced updateQuantity with persistence
  const updateQuantity = useCallback((productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }

    setCart(prevCart => prevCart.map(item =>
      item.id === productId
        ? { ...item, quantity: newQuantity, updatedAt: new Date().toISOString() }
        : item
    ));
  }, [removeFromCart]);

  const handleVariationSelect = useCallback((productId, variation) => {
    setSelectedVariations(prev => ({
      ...prev,
      [productId]: variation
    }));
  }, []);

  // Fetch related products with pagination
  const fetchRelatedProducts = useCallback(async (page = 1, append = false) => {
    try {
      setLoadingMore(true);
      const response = await api.get(
        `${API_CONFIG.PRODUCTS.SHARED_PRODUCT}/${productId}?relatedPage=${page}&relatedLimit=12`
      );

      if (response.data.success) {
        const { products, pagination } = response.data.relatedProducts;
        
        if (append) {
          setRelatedProducts(prev => [...prev, ...products]);
        } else {
          setRelatedProducts(products);
        }
        
        setRelatedPagination(pagination);
        setAllRelatedProductsLoaded(!pagination.hasNext);
      }
    } catch (error) {
      console.error('Error fetching related products:', error);
      showToast('Failed to load more products', 'error');
    } finally {
      setLoadingMore(false);
    }
  }, [productId]);

  // Load more related products
  const loadMoreRelatedProducts = useCallback(() => {
    if (!loadingMore && relatedPagination.hasNext) {
      const nextPage = relatedPagination.currentPage + 1;
      fetchRelatedProducts(nextPage, true);
    }
  }, [loadingMore, relatedPagination, fetchRelatedProducts]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (allRelatedProductsLoaded || loadingMore) return;

    const relatedProductsSection = document.getElementById('related-products-section');
    if (relatedProductsSection) {
      const { bottom } = relatedProductsSection.getBoundingClientRect();
      const { innerHeight } = window;
      
      // Load more when user is 200px from bottom of related products section
      if (bottom - innerHeight < 200) {
        loadMoreRelatedProducts();
      }
    }
  }, [allRelatedProductsLoaded, loadingMore, loadMoreRelatedProducts]);

  // Add scroll event listener
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Product metadata
  const productMetadata = useMemo(() => {
    if (!product) return null;

    const productImages = getProductImages(product);
    const selectedVariation = selectedVariations[product.id];
    const availableStock = getAvailableStock(product, selectedVariation);
    const isNegotiable = hasNegotiablePricing(product);
    const priceDisplay = getPriceDisplay(product);
    const isDigital = isDigitalProduct(product);

    const title = `${product.product_name} - ${companyData.company_name || 'Product'}`;
    const description = product.description
      ? `${product.description.substring(0, 160)}...`
      : `Buy ${product.product_name} from ${companyData.company_name}. ${priceDisplay}. Secure shopping with fast delivery.`;

    const image = productImages.length > 0 ? productImages[0] : `${baseUrl}/default-product-image.jpg`;

    return {
      title,
      description,
      image,
      url: shareLink,
      productName: product.product_name,
      price: product.per_unit_cost,
      currency: product.currency,
      condition: getConditionLabel(product.product_condition),
      brand: product.brand,
      availability: availableStock > 0 ? 'InStock' : 'OutOfStock',
      productImages,
      isNegotiable,
      priceDisplay,
      isDigital
    };
  }, [product, companyData, baseUrl, shareLink, getConditionLabel, getProductImages, getAvailableStock, selectedVariations, hasNegotiablePricing, getPriceDisplay, isDigitalProduct]);

  // Meta tags management
  useEffect(() => {
    if (productMetadata && product && companyData) {
      document.title = productMetadata.title;

      const setMetaTag = (name, content, isProperty = false) => {
        const attr = isProperty ? 'property' : 'name';
        let meta = document.querySelector(`meta[${attr}="${name}"]`);

        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(attr, name);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };

      setMetaTag('description', productMetadata.description);
      setMetaTag('og:title', productMetadata.title, true);
      setMetaTag('og:description', productMetadata.description, true);
      setMetaTag('og:image', productMetadata.image, true);
      setMetaTag('og:url', productMetadata.url, true);
      setMetaTag('og:type', 'product', true);
      setMetaTag('og:site_name', companyData.company_name || 'Paymeo Store', true);
      setMetaTag('product:price:amount', productMetadata.price.toString(), true);
      setMetaTag('product:price:currency', productMetadata.currency, true);
      setMetaTag('product:availability', productMetadata.availability.toLowerCase(), true);
      setMetaTag('product:condition', productMetadata.condition.toLowerCase(), true);

      if (productMetadata.isNegotiable) {
        setMetaTag('product:price:negotiable', 'true', true);
      }

      // Add digital product meta tags
      if (productMetadata.isDigital) {
        setMetaTag('product:category', 'digital', true);
        setMetaTag('product:delivery', 'instant', true);
      }

      setMetaTag('twitter:card', 'summary_large_image', true);
      setMetaTag('twitter:title', productMetadata.title, true);
      setMetaTag('twitter:description', productMetadata.description, true);
      setMetaTag('twitter:image', productMetadata.image, true);

      const keywords = [
        productMetadata.productName,
        companyData.company_name,
        productMetadata.brand,
        productMetadata.condition,
        productMetadata.isDigital ? 'digital download' : '',
        'buy online',
        'shopping',
        'ecommerce'
      ].filter(Boolean).join(', ');
      setMetaTag('keywords', keywords);

      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', productMetadata.url);

      const structuredData = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.product_name,
        "description": product.description,
        "image": productMetadata.productImages,
        "sku": product.sku,
        "brand": {
          "@type": "Brand",
          "name": product.brand || companyData.company_name
        },
        "offers": {
          "@type": "Offer",
          "url": productMetadata.url,
          "priceCurrency": product.currency,
          "price": product.per_unit_cost,
          "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          "availability": `https://schema.org/${productMetadata.availability}`,
          "itemCondition": `https://schema.org/${productMetadata.condition}Condition`,
          "seller": {
            "@type": "Organization",
            "name": companyData.company_name
          }
        }
      };

      // Add digital product structured data
      if (productMetadata.isDigital) {
        structuredData.additionalType = "https://schema.org/DigitalProduct";
        structuredData.offers.deliveryMethod = "https://schema.org/Download";
      }

      if (hasNegotiablePricing(product)) {
        structuredData.offers.priceSpecification = {
          "@type": "PriceSpecification",
          "minPrice": product.starting_price,
          "maxPrice": product.ending_price,
          "priceCurrency": product.currency
        };
      }

      let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
      if (!structuredDataScript) {
        structuredDataScript = document.createElement('script');
        structuredDataScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(structuredDataScript);
      }
      structuredDataScript.textContent = JSON.stringify(structuredData);

      return () => {
        document.title = 'Paymeo';
      };
    }
  }, [productMetadata, product, companyData, hasNegotiablePricing]);

  // Fetch product data
  useEffect(() => {
    const fetchProductData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(
          `${API_CONFIG.PRODUCTS.SHARED_PRODUCT}/${productId}`
        );

        if (response.data.success) {
          setProduct(response.data.product);
          setCompany(response.data.company || {});
          
          // Handle both old and new response formats
          if (response.data.relatedProducts) {
            setRelatedProducts(response.data.relatedProducts.products || []);
            setRelatedPagination(response.data.relatedProducts.pagination || {
              currentPage: 1,
              totalPages: 0,
              totalProducts: 0,
              hasNext: false,
              hasPrev: false,
              limit: 12
            });
            setAllRelatedProductsLoaded(!response.data.relatedProducts.pagination?.hasNext);
          } else {
            // Fallback to old format
            setRelatedProducts(response.data.otherProducts || []);
            setRelatedPagination(prev => ({
              ...prev,
              totalProducts: response.data.otherProducts?.length || 0,
              hasNext: false
            }));
            setAllRelatedProductsLoaded(true);
          }

    
if (response.data.product.variations && Object.keys(response.data.product.variations).length > 0) {
  setSelectedVariations(prev => ({
    ...prev,
    [response.data.product.id]: {}
  }));
}
        } else {
          showToast('Product not found', 'error');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        showToast('Failed to load product', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    if (productId) fetchProductData();
  }, [productId]);

  useEffect(() => {
  if (product && companyData) {
    // Make product data available globally for the AI agent
    window.__PRODUCT_DATA__ = product;
    window.__COMPANY_DATA__ = companyData;
  }
}, [product, companyData]);

  const handleImageError = useCallback(() => setImageError(true), []);

  // Get current product images
  const productImages = useMemo(() => getProductImages(product), [getProductImages, product]);

  // Image navigation functions
  const nextImage = useCallback(() => {
    setSelectedImage((prev) => (prev + 1) % productImages.length);
  }, [productImages.length]);

  const prevImage = useCallback(() => {
    setSelectedImage((prev) => (prev - 1 + productImages.length) % productImages.length);
  }, [productImages.length]);

  // Keyboard navigation for image modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showImageModal) return;

      switch (e.key) {
        case 'ArrowLeft':
          prevImage();
          break;
        case 'ArrowRight':
          nextImage();
          break;
        case 'Escape':
          setShowImageModal(false);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showImageModal, prevImage, nextImage]);

  // Loading component
  const LoadingState = () => (
    <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-white">
      <Header cartItemCount={0} />
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-square bg-gray-200 dark:bg-gray-800 rounded-2xl"></div>
            <div className="space-y-6">
              <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-3/4"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-1/2"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-full"></div>
              <div className="h-12 bg-gray-200 dark:bg-gray-800 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Error state component
  const ErrorState = () => (
    <div className="min-h-screen bg-white dark:bg-black flex items-center justify-center text-gray-900 dark:text-white">
      <div className="text-center">
        <div className="w-full flex justify-center rounded-2xl overflow-hidden mb-4">
           <div className="w-24 h-24 bg-gray-100 dark:bg-gray-900 rounded-full flex items-center justify-center">
             <ShoppingBag className="w-10 h-10 text-gray-400" />
           </div>
        </div>
        <h1 className="text-lg font-bold mb-2 text-xl">Product Not Found</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">This product is no longer available.</p>
        <Link
          to="/"
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors font-bold text-sm"
        >
          Continue Shopping
        </Link>
      </div>
    </div>
  );

  // Image Preview Modal Component
  const ImagePreviewModal = () => {
    if (!showImageModal) return null;

    const hasImages = productImages.length > 0;
    const hasMultipleImages = productImages.length > 1;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm">
        <div className="relative w-full max-w-6xl max-h-full flex flex-col items-center">
          {/* Close Button */}
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-4 right-4 z-20 w-10 h-10 bg-black/50 text-white rounded-full hover:bg-black/70 transition-all flex items-center justify-center"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation Arrows */}
          {hasMultipleImages && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-black/50 text-white rounded-full hover:bg-black/70 transition-all flex items-center justify-center"
              >
                <ChevronLeft className="w-8 h-8" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 w-12 h-12 bg-black/50 text-white rounded-full hover:bg-black/70 transition-all flex items-center justify-center"
              >
                 <ChevronRight className="w-8 h-8" />
              </button>
            </>
          )}

          {/* Main Image */}
          <div className="flex items-center justify-center h-full w-full">
            {hasImages && !imageError ? (
              <img
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                src={productImages[selectedImage]}
                alt={product.product_name}
                onError={handleImageError}
              />
            ) : (
              <div className="w-32 h-32 flex items-center justify-center text-gray-400">
                 <ShoppingBag className="w-16 h-16" />
              </div>
            )}
          </div>

          {/* Image Counter */}
          {hasMultipleImages && (
            <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 bg-black/70 text-white text-sm font-bold px-4 py-1 rounded-full backdrop-blur-md">
              {selectedImage + 1} / {productImages.length}
            </div>
          )}

          {/* Thumbnail Previews */}
          {hasMultipleImages && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2 overflow-x-auto max-w-full px-4 no-scrollbar">
              {productImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`flex-shrink-0 w-16 h-16 border-2 rounded-lg overflow-hidden transition-all ${selectedImage === index
                      ? 'border-blue-500 scale-110 ring-2 ring-blue-500/50'
                      : 'border-gray-600 hover:border-gray-400 opacity-70 hover:opacity-100'
                    }`}
                >
                  <img
                    src={image}
                    alt={`${product.product_name} view ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Image gallery component
  const ImageGallery = () => {
    const hasImages = productImages.length > 0;
    const hasMultipleImages = productImages.length > 1;
    const isDigital = isDigitalProduct(product);

    return (
      <div className="space-y-4">
        {/* Main Image - Click to open modal */}
        <div
          className="aspect-square bg-gray-100 dark:bg-gray-900 rounded-3xl shadow-lg overflow-hidden relative cursor-zoom-in group border border-gray-200 dark:border-gray-800"
          onClick={() => setShowImageModal(true)}
        >
          {hasImages && !imageError ? (
            <>
              <img
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                src={productImages[selectedImage]}
                alt={product.product_name}
                onError={handleImageError}
              />

              {/* Digital Product Badge */}
              {isDigital && (
                <div className="absolute top-4 left-4 bg-purple-600 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg z-10">
                  Digital Product
                </div>
              )}

              {/* Negotiable Badge */}
              {isNegotiable && !agreedPrice && (
                <div className={`absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg select-none ${
                  isNegotiating
                    ? 'bg-black text-white animate-pulse'
                    : 'bg-black text-white'
                }`}>
                  {isNegotiating ? 'Active deal' : 'Negotiable'}
                </div>
              )}

              {/* Image Navigation */}
              {hasMultipleImages && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      prevImage();
                    }}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 text-white rounded-full hover:bg-black/70 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      nextImage();
                    }}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/50 text-white rounded-full hover:bg-black/70 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>

                  {/* Image Counter */}
                  <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-md text-white text-xs font-bold px-3 py-1.5 rounded-full">
                    {selectedImage + 1} / {productImages.length}
                  </div>
                </>
              )}

              {/* Zoom Icon Overlay */}
              <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-10 h-10 bg-black/50 text-white rounded-full flex items-center justify-center backdrop-blur-sm">
                   <Maximize className="w-5 h-5" />
                </div>
              </div>
            </>
          ) : (
            <div className={`w-full h-full flex items-center justify-center ${isDigital ? 'bg-purple-50 dark:bg-purple-900/10' : 'bg-gray-100 dark:bg-gray-800'}`}>
              <div className="text-center">
                 <ShoppingBag className={`w-16 h-16 mx-auto ${isDigital ? 'text-purple-300' : 'text-gray-300 dark:text-gray-600'}`} />
                {isDigital && (
                  <p className="text-purple-600 dark:text-purple-400 text-sm font-bold mt-2">Digital Product</p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Image Thumbnails */}
        {hasMultipleImages && (
          <div className="flex space-x-3 overflow-x-auto py-2 no-scrollbar">
            {productImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setSelectedImage(index)}
                className={`flex-shrink-0 w-20 h-20 border-2 rounded-xl overflow-hidden transition-all ${selectedImage === index ? 'border-blue-600 scale-105 shadow-md' : 'border-gray-200 dark:border-gray-700 hover:border-blue-400 opacity-70 hover:opacity-100'
                  }`}
              >
                <img
                  src={image}
                  alt={`${product.product_name} view ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Product variations component - Hide for digital products
// Replace the existing ProductVariations component with this new one:

// Enhanced Product variations component with grouping - Hide for digital products
// Enhanced Product variations component with grouping - Hide for digital products
const ProductVariations = () => {
  // Check if product has variations in the new grouped format
  const hasGroupedVariations = product.variations && 
                               typeof product.variations === 'object' && 
                               Object.keys(product.variations).length > 0;
  
  // Check if product has variations in the old flat array format
  const hasArrayVariations = product.variations && 
                             Array.isArray(product.variations) && 
                             product.variations.length > 0;
  
  if (!hasGroupedVariations && !hasArrayVariations) return null;
  if (isDigitalProduct(product)) return null;

  const selectedVariation = selectedVariations[product.id] || {};

  // Handle variation selection
  const handleVariationSelect = (variationType, variation) => {
    setSelectedVariations(prev => {
      const currentSelections = prev[product.id] || {};
      
      // Create new selections object
      const newSelections = {
        ...currentSelections,
        [variationType]: variation
      };

      return {
        ...prev,
        [product.id]: newSelections
      };
    });
  };

  // Helper to get variation display name
  const getVariationDisplayName = (type) => {
    const names = {
      'color': 'Color',
      'size': 'Size',
      'material': 'Material',
      'pattern': 'Pattern',
      'style': 'Style'
    };
    return names[type.toLowerCase()] || type;
  };

  // Check if a variation option is selected
  const isVariationSelected = (variationType, variationId) => {
    return selectedVariation[variationType]?.variation_id === variationId;
  };

  // Get current selected variations for display
  const getSelectedVariationsDisplay = () => {
    return Object.entries(selectedVariation)
      .map(([type, variation]) => `${getVariationDisplayName(type)}: ${variation.variation_value}`)
      .join(', ');
  };

  // Render grouped variations
  if (hasGroupedVariations) {
    return (
      <div className="space-y-4">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">Select Options</h3>
        {Object.entries(product.variations).map(([variationType, variations]) => (
          <div key={variationType} className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
                {getVariationDisplayName(variationType)}
              </span>
              {selectedVariation[variationType] && (
                <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                  Selected: {selectedVariation[variationType].variation_value}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {variations.map((variation) => {
                const isSelected = isVariationSelected(variationType, variation.variation_id);
                const isOutOfStock = variation.inventory <= 0;

                return (
                  <button
                    key={variation.variation_id}
                    onClick={() => !isOutOfStock && handleVariationSelect(variationType, variation)}
                    disabled={isOutOfStock}
                    className={`px-4 py-2 rounded-lg border-2 text-sm font-bold transition-all ${isSelected
                        ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                        : isOutOfStock
                          ? 'border-gray-200 dark:border-gray-800 text-gray-400 cursor-not-allowed bg-gray-50 dark:bg-gray-900'
                          : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-black'
                      }`}
                  >
                    {variation.variation_value}
                    {variation.price_adjustment > 0 && (
                      <span className="ml-1 font-normal opacity-90">
                        +{formatCurrency(variation.price_adjustment, product.currency)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Render old flat array variations (backward compatibility)
  if (hasArrayVariations) {
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">Select Option</h3>
        <div className="flex flex-wrap gap-2">
          {product.variations.map((variation) => {
            // For backward compatibility, treat as single variation type
            const isSelected = selectedVariation && selectedVariation.variation_id === variation.variation_id;
            const isOutOfStock = variation.inventory <= 0;

            return (
              <button
                key={variation.variation_id}
                onClick={() => !isOutOfStock && handleVariationSelect('type', variation)}
                disabled={isOutOfStock}
                className={`px-4 py-2 rounded-lg border-2 text-sm font-bold transition-all ${isSelected
                    ? 'border-blue-600 bg-blue-600 text-white shadow-md'
                    : isOutOfStock
                      ? 'border-gray-200 dark:border-gray-800 text-gray-400 cursor-not-allowed bg-gray-50 dark:bg-gray-900'
                      : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-400 dark:hover:border-blue-500 bg-white dark:bg-black'
                  }`}
              >
                {variation.variation_value}
                {variation.price_adjustment > 0 && (
                  <span className="ml-1 font-normal opacity-90">
                    +{formatCurrency(variation.price_adjustment, product.currency)}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return null;
};

  // Quantity selector component - Hide for services and digital products
  const QuantitySelector = () => {
    const selectedVariation = selectedVariations[product.id];
    const availableStock = getAvailableStock(product, selectedVariation);

    const listingType = getListingTypeDisplay(product);
    const isDigital = isDigitalProduct(product);

    // Don't show quantity selector for services or digital products
    if (listingType === 'Service' || isDigital) return null;

    return (
      <div className="space-y-3">
        <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">Quantity</h3>
        <div className="flex items-center space-x-4">
          <div className="flex items-center border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-black">
            <button
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={quantity <= 1}
              className="px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white disabled:opacity-30 transition-colors"
            >
              −
            </button>
            <span className="px-4 py-2 border-l border-r border-gray-300 dark:border-gray-700 min-w-[3rem] text-center font-bold text-black dark:text-white">
              {quantity}
            </span>
            <button
              onClick={() => setQuantity(Math.min(availableStock, quantity + 1))}
              disabled={quantity >= availableStock}
              className="px-3 py-2 text-gray-600 dark:text-gray-400 hover:text-black dark:hover:text-white disabled:opacity-30 transition-colors"
            >
              +
            </button>
          </div>
          <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">
            {availableStock} available
          </span>
        </div>
      </div>
    );
  };

  // Trust badges component
  const TrustBadges = () => (
    <div className="flex items-center justify-center space-x-8 pt-6 border-t border-gray-200 dark:border-gray-800 mt-6">
      {TRUST_BADGES.map((badge, index) => (
        <div key={index} className="text-center flex flex-col items-center gap-2">
          <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-full">
            {badge.icon}
          </div>
          <span className="text-xs font-bold text-gray-600 dark:text-gray-400">{badge.label}</span>
        </div>
      ))}
    </div>
  );

  // Related products component with pagination
  const RelatedProducts = () => {
    if (relatedProducts.length === 0 && !relatedPagination.hasNext) return null;

    return (
      <section id="related-products-section" className="mb-16 pt-10 border-t border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            More from {companyData.company_name}
            {relatedPagination.totalProducts > 0 && (
              <span className="text-gray-500 dark:text-gray-400 font-medium ml-2 text-sm">
                ({relatedPagination.totalProducts})
              </span>
            )}
          </h2>
          <Link
            to={`/s/${companyData.username || companyData.company_id}`}
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-bold text-sm transition-colors flex items-center gap-1"
          >
            View all products <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {relatedProducts.map((relatedProduct) => {
            // const selectedVariation = selectedVariations[relatedProduct.id];
            // const availableStock = getAvailableStock(relatedProduct, selectedVariation);
            // const finalPrice = selectedVariation
            //   ? (relatedProduct.per_unit_cost || 0) + (selectedVariation.price_adjustment || 0)
            //   : relatedProduct.per_unit_cost || 0;

            const selectedVariationsForProduct = selectedVariations[relatedProduct.id] || {};
const availableStock = getAvailableStock(relatedProduct, selectedVariationsForProduct);
const finalPrice = getFinalPrice(relatedProduct, selectedVariationsForProduct);
            const productImage = getOtherProductImage(relatedProduct);
            const listingType = getListingTypeDisplay(relatedProduct);
            const isDigital = isDigitalProduct(relatedProduct);

            return (
              <div
                key={`${relatedProduct.id}-${relatedProduct.product_id}`}
                className="group border border-gray-200 dark:border-gray-800 rounded-2xl hover:shadow-xl transition-all duration-300 bg-white dark:bg-[#0a0a0a] overflow-hidden hover:border-gray-300 dark:hover:border-gray-700"
              >
                <div className={`aspect-square rounded-t-2xl overflow-hidden relative ${isDigital ? 'bg-purple-50 dark:bg-purple-900/10' : 'bg-gray-100 dark:bg-gray-900'}`}>
                  {productImage ? (
                    <Link to={`/product/${relatedProduct.slug}`}>
                      <img
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        src={productImage}
                        alt={relatedProduct.product_name}
                      />
                    </Link>
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center ${isDigital ? 'text-purple-300' : 'text-gray-300 dark:text-gray-700'}`}>
                       <ShoppingBag className="w-12 h-12" />
                    </div>
                  )}
                  {/* Digital Product Badge */}
                  {isDigital && (
                    <div className="absolute top-3 left-3 bg-purple-600 text-white px-2 py-1 rounded-full text-[10px] font-bold shadow-sm">
                      Digital
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <h3 className="font-bold text-gray-900 dark:text-white mb-2 line-clamp-1 text-sm">
                    <Link to={`/product/${relatedProduct.slug || relatedProduct.id}`} className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      {relatedProduct.product_name}
                    </Link>
                  </h3>

                  <div className="flex items-center justify-between mb-4">
                    <span className="font-bold text-gray-900 dark:text-white">
                      {formatCurrency(finalPrice, relatedProduct.currency)}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                      isDigital
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                        : listingType === 'Service' 
                          ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                          : availableStock > 0 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {isDigital ? 'Digital' : 
                       listingType === 'Service' ? 'Service' : 
                       availableStock > 0 ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>

                  <button
                    onClick={() => addToCart(relatedProduct, selectedVariationsForProduct)}
                    disabled={!isDigital && listingType !== 'Service' && availableStock <= 0}
                    className={`w-full py-2.5 px-4 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm ${
                      isDigital
                        ? 'bg-purple-600 text-white hover:bg-purple-700'
                        : listingType === 'Service'
                          ? 'bg-orange-600 text-white hover:bg-orange-700'
                          : 'bg-black dark:bg-white text-white dark:text-black hover:opacity-90'
                    }`}
                  >
                    {isDigital ? 'Add to Cart' :
                     listingType === 'Service' ? 'Book Now' : 'Add to Cart'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Load More Button */}
        {relatedPagination.hasNext && (
          <div className="flex justify-center mt-10">
            <button
              onClick={loadMoreRelatedProducts}
              disabled={loadingMore}
              className="px-8 py-3 bg-gray-100 dark:bg-gray-900 text-black dark:text-white rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors font-bold text-sm disabled:opacity-50"
            >
              {loadingMore ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                  <span>Loading...</span>
                </div>
              ) : (
                'Load More Products'
              )}
            </button>
          </div>
        )}

        {/* All products loaded message */}
        {allRelatedProductsLoaded && relatedProducts.length > 0 && (
          <div className="text-center mt-8">
            <p className="text-gray-400 dark:text-gray-600 text-xs font-medium uppercase tracking-wider">
              No more Products to display
            </p>
          </div>
        )}
      </section>
    );
  };

  // Delivery Options Component - Hide for digital products
  const DeliveryOptions = () => {
    if (isDigitalProduct(product)) return null;

    const deliveryOptions = getDeliveryOptions(product);

    if (!deliveryOptions) {
      return (
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4 text-center">
          <p className="text-gray-500 dark:text-gray-400 text-sm">No delivery options configured by seller.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {/* Pickup Option */}
        {deliveryOptions.pickup?.available && (
          <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800/30 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                   <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h4 className="font-bold text-green-900 dark:text-green-300 text-sm">Customer Pickup</h4>
                  <p className="text-green-700 dark:text-green-400 text-xs">Available at seller's location</p>
                </div>
              </div>
              <span className="text-green-600 dark:text-green-400 font-bold text-sm">Free</span>
            </div>
          </div>
        )}

        {/* Delivery Option */}
        {deliveryOptions.delivery?.available && (
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                   <Truck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h4 className="font-bold text-blue-900 dark:text-blue-300 text-sm">Home Delivery</h4>
                  <p className="text-blue-700 dark:text-blue-400 text-xs">Delivered to your address</p>
                </div>
              </div>
            </div>

            {/* Delivery Fees */}
            <div className="space-y-2 pl-11 border-t border-blue-100 dark:border-blue-800/30 pt-2 mt-2">
              {lagosDeliveryFee(deliveryOptions.delivery.fees?.lagos, product.currency)}
              {outsideLagosDelivery(deliveryOptions.delivery.fees?.outside_lagos, product.currency)}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Tab content component with digital product support
  const TabContent = () => {
    const isDigital = isDigitalProduct(product);

    switch (activeTab) {
      case TABS.SPECIFICATIONS:
        return (
          <div className="space-y-8 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h3 className="font-bold text-gray-900 dark:text-white text-lg">Product Details</h3>
                <div className="space-y-4 bg-gray-50 dark:bg-gray-900/50 p-5 rounded-2xl border border-gray-100 dark:border-gray-800">
                  <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                    <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Type</span>
                    <span className="font-bold text-gray-900 dark:text-white text-sm">
                      {isDigital ? 'Digital Product' : getListingTypeDisplay(product)}
                    </span>
                  </div>
                  {!isDigital && (
                    <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                      <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Condition</span>
                      <span className="font-bold text-gray-900 dark:text-white text-sm">
                        {getConditionLabel(product.product_condition)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                    <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Brand</span>
                    <span className="font-bold text-gray-900 dark:text-white text-sm">
                      {product.brand || 'Not specified'}
                    </span>
                  </div>
                  <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                    <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">SKU</span>
                    <span className="font-bold text-gray-900 dark:text-white text-sm font-mono">
                      {product.sku || 'N/A'}
                    </span>
                  </div>
                  {hasNegotiablePricing(product) && (
                    <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                      <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Price Type</span>
                      <span className="font-bold text-green-600 dark:text-green-400 text-sm">
                        Negotiable
                      </span>
                    </div>
                  )}
                  {/* Digital Product Specific Details */}
                  {isDigital && product.digital_product_type && (
                    <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                      <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">File Type</span>
                      <span className="font-bold text-gray-900 dark:text-white text-sm">
                        {product.digital_product_type}
                      </span>
                    </div>
                  )}
                  {isDigital && product.file_size && (
                    <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                      <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">File Size</span>
                      <span className="font-bold text-gray-900 dark:text-white text-sm">
                        {product.file_size}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-6">
                <h3 className="font-bold text-gray-900 dark:text-white text-lg">
                  {isDigital ? 'Download & Access' : 'Shipping & Policy'}
                </h3>
                <div className="space-y-4 bg-gray-50 dark:bg-gray-900/50 p-5 rounded-2xl border border-gray-100 dark:border-gray-800">
                  {isDigital ? (
                    <>
                      <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                        <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Delivery Mode</span>
                        <span className="font-bold text-purple-600 dark:text-purple-400 text-sm">
                          Instant Download
                        </span>
                      </div>
                      {product.allowed_downloads && (
                        <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                          <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Max Downloads</span>
                          <span className="font-bold text-gray-900 dark:text-white text-sm">
                            {product.allowed_downloads} times
                          </span>
                        </div>
                      )}
                      {product.download_expiry_days && (
                        <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                          <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Link Expiry</span>
                          <span className="font-bold text-gray-900 dark:text-white text-sm">
                            {product.download_expiry_days} days
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                        <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Return Policy</span>
                        <span className="font-bold text-gray-900 dark:text-white text-sm">
                          Non-refundable
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                        <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Est. Delivery</span>
                        <span className="font-bold text-gray-900 dark:text-white text-sm">
                          {getDeliveryTimelineLabel(product.delivery_timeline)}
                        </span>
                      </div>
                      <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                        <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Return Policy</span>
                        <span className="font-bold text-gray-900 dark:text-white text-sm">
                          {product.return_policy === '0' ? 'No returns' : `${product.return_policy} days`}
                        </span>
                      </div>
                      <div className="flex justify-between border-b border-gray-200 dark:border-gray-800 pb-3 last:border-0 last:pb-0">
                        <span className="text-gray-500 dark:text-gray-400 text-sm font-medium">Methods</span>
                        <span className="font-bold text-gray-900 dark:text-white text-sm text-right max-w-[150px]">
                          {getDeliverySummary(product)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case TABS.DELIVERY:
        return (
          <div className="space-y-6 animate-fade-in">
            <h3 className="font-bold text-gray-900 dark:text-white text-lg">
              {isDigitalProduct(product) ? 'Download Information' : 'Delivery & Pickup Options'}
            </h3>
            
            {isDigitalProduct(product) ? (
              <div className="space-y-4">
                <div className="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800/30 rounded-xl p-5">
                  <h4 className="font-bold text-purple-900 dark:text-purple-300 mb-3 text-sm">Instant Digital Download</h4>
                  <div className="text-sm text-purple-700 dark:text-purple-400 space-y-2">
                    <p className="flex items-center gap-2"><Check className="w-4 h-4" /> Available immediately after payment</p>
                    <p className="flex items-center gap-2"><Check className="w-4 h-4" /> Secure download with PIN protection</p>
                    <p className="flex items-center gap-2"><Check className="w-4 h-4" /> High-speed download servers</p>
                    {product.allowed_downloads && (
                      <p className="flex items-center gap-2"><Check className="w-4 h-4" /> {product.allowed_downloads} download attempts allowed</p>
                    )}
                    {product.download_expiry_days && (
                      <p className="flex items-center gap-2"><Check className="w-4 h-4" /> Download link valid for {product.download_expiry_days} days</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <DeliveryOptions />

                {/* Service-specific information */}
                {getListingTypeDisplay(product) === 'Service' && product.upfront_payment_percentage > 0 && (
                  <div className="bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/30 rounded-xl p-4">
                    <h4 className="text-sm font-bold text-orange-900 dark:text-orange-300 mb-2">Service Payment Terms</h4>
                    <div className="text-sm text-orange-800 dark:text-orange-400">
                      <strong>{product.upfront_payment_percentage}%</strong> upfront payment required for service booking to secure your slot.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case TABS.REVIEWS:
        return (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-gray-800">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-blue-600 dark:text-blue-400 fill-current" />
              </div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-2 text-lg">Customer Reviews</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6 text-sm">
                This product has an average rating of 4.8 from 24 reviews
              </p>
              <button className="px-6 py-2.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors font-bold text-sm shadow-lg" onClick={() => showToast("Reviews coming soon", "info")}>
                Write a Review
              </button>
            </div>
          </div>
        );

      default: // DESCRIPTION
        return (
          <div className="prose dark:prose-invert max-w-none animate-fade-in">
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line text-sm">
              {product.description || 'No description available.'}
            </p>
            
            {/* Digital Product Additional Info */}
            {isDigitalProduct(product) && (
              <div className="mt-8 p-5 bg-purple-50 dark:bg-purple-900/10 border border-purple-100 dark:border-purple-800/30 rounded-xl">
                <h4 className="font-bold text-purple-900 dark:text-purple-300 mb-3 text-sm">About This Digital Product</h4>
                <ul className="text-purple-800 dark:text-purple-400 space-y-2 text-sm">
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5" /> Instant access after payment confirmation</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5" /> Download securely with PIN protection</li>
                  <li className="flex items-center gap-2"><Check className="w-3.5 h-3.5" /> High-quality original file</li>
                </ul>
              </div>
            )}
          </div>
        );
    }
  };

  // Product tabs component
  const ProductTabs = () => (
    <div className="border-b border-gray-200 dark:border-gray-800 mb-8">
      <nav className="flex space-x-8 overflow-x-auto no-scrollbar">
        {[
          { id: TABS.DESCRIPTION, label: 'Description' },
          { id: TABS.SPECIFICATIONS, label: 'Specifications' },
          { id: TABS.DELIVERY, label: isDigitalProduct(product) ? 'Download Info' : 'Delivery Options' },
          { id: TABS.REVIEWS, label: 'Reviews' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`py-4 px-1 border-b-2 font-bold text-sm transition-colors whitespace-nowrap ${activeTab === tab.id
                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </nav>
    </div>
  );

  // Action buttons component - Enhanced for digital products
  const ActionButtons = () => {
    const selectedVariation = selectedVariations[product.id];
    const availableStock = getAvailableStock(product, selectedVariation);
    const listingType = getListingTypeDisplay(product);
    const isDigital = isDigitalProduct(product);

    if (listingType === 'Service') {
      return (
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-100 dark:border-gray-800">
          <button
            onClick={() => setShowServiceBookingModal(true)}
            className="flex-1 bg-orange-600 text-white py-3.5 px-6 rounded-full hover:bg-orange-700 transition-colors font-bold shadow-lg text-sm"
          >
            Book Now
          </button>
          <button
            onClick={() => setShowContactModal(true)}
            className="flex-1 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white py-3.5 px-6 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-bold text-sm"
          >
            Contact Seller
          </button>
        </div>
      );
    }

    // Digital Product Action Buttons
    if (isDigital) {
      return (
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-100 dark:border-gray-800">
          <button
            onClick={() => addToCart(product, selectedVariation)}
            className="flex-1 bg-purple-600 text-white py-3.5 px-6 rounded-full hover:bg-purple-700 transition-colors font-bold shadow-lg text-sm"
          >
            Add to Cart - Instant Download
          </button>
          <button
            onClick={() => setShowContactModal(true)}
            className="flex-1 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white py-3.5 px-6 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-bold text-sm"
          >
            Contact Seller
          </button>
        </div>
      );
    }

    // Physical Product Action Buttons
    return (
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-100 dark:border-gray-800">
        <button
          onClick={() => addToCart(product, selectedVariation)}
          disabled={availableStock <= 0 || (product.variations && product.variations.length > 0 && !selectedVariation)}
          className="flex-1 bg-blue-600 text-white py-3.5 px-6 rounded-full hover:bg-blue-700 transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg text-sm"
        >
          {availableStock <= 0 ? 'Out of Stock' :
            product.variations && product.variations.length > 0 && !selectedVariation ? 'Select Option' : 'Add to Cart'}
        </button>
        <button
          onClick={() => setShowContactModal(true)}
          className="flex-1 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-white py-3.5 px-6 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-bold text-sm"
        >
          Contact Seller
        </button>
      </div>
    );
  };

  if (isLoading) return <LoadingState />;
  if (!product) return <ErrorState />;

  // Get selected variation and pricing
  const selectedVariation = selectedVariations[product.id];
  const availableStock = getAvailableStock(product, selectedVariation);
  const finalPrice = selectedVariation
    ? product.per_unit_cost + (selectedVariation.price_adjustment || 0)
    : product.per_unit_cost || 0;

  const isNegotiable = hasNegotiablePricing(product);
  const listingType = getListingTypeDisplay(product);
  const isDigital = isDigitalProduct(product);

  return (
    <>
      <div className="min-h-screen bg-white dark:bg-black text-black dark:text-white transition-colors duration-200 font-sans">
        {/* Meta Tags Debugger */}
        <MetaTagDebugger />

        <Header
          setShowCartModal={setShowCartModal}
          setShowContactModal={setShowContactModal}
          cartItemCount={cartItemCount}
          setShowShareStoreModal={setShowShareStoreModal}
        />

        {/* Breadcrumb Navigation */}
        <nav className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0a0a0a] sticky top-[60px] z-40">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400" style={{ fontSize: '13px' }}>
              <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">Home</Link>
              <span>/</span>
              <Link to={`/s/${companyData.username || companyData.company_id}`} className="hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                {companyData.company_name || 'Store'}
              </Link>
              <span>/</span>
              <span className="text-gray-900 dark:text-white truncate font-medium">{product.product_name}</span>
            </div>
          </div>
        </nav>

        <div className="max-w-6xl mx-auto px-4 py-8 xl:mb-0 mb-[200px]">
          {/* Main Product Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Product Images */}
            <ImageGallery />

            {/* Product Details */}
            <div className="space-y-6">
              {/* Seller Info */}
              {companyData.company_name && (
                <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-gray-800">
                  <Link
                    to={`/s/${companyData.username || companyData.company_id}`}
                    className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors font-bold text-sm"
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    {companyData.company_name}
                  </Link>

                  {/* Trust Signals */}
                  <div className="flex items-center space-x-4 text-gray-500 dark:text-gray-400 text-xs font-medium">
                    {getProductTypeBadge(product)}
                    <span className="flex items-center gap-1"><Star className="w-3 h-3 text-yellow-500 fill-current" /> 4.8</span>
                    <span>•</span>
                    <span>{isDigital ? '🚀 Instant' : `🚚 ${getDeliverySummary(product)}`}</span>
                  </div>
                </div>
              )}

              {/* Product Title */}
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white leading-tight tracking-tight">
                {product.product_name}
              </h1>

              {/* Price Section */}
              <div className="flex items-baseline space-x-3 flex-wrap gap-y-1">
                {!agreedPrice && (
                  <span className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                    {formatCurrency(finalPrice, product.currency)}
                  </span>
                )}
                {agreedPrice && (
                  <>
                    <span className="text-gray-400 dark:text-gray-600 line-through font-medium text-lg">
                      {formatCurrency(finalPrice, product.currency)}
                    </span>
                    <span className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 tracking-tight">
                      {formatCurrency(agreedPrice, product.currency)}
                    </span>
                  </>
                )}
                {selectedVariation && selectedVariation.price_adjustment > 0 && !isNegotiable && (
                  <span className="text-gray-400 dark:text-gray-600 line-through font-medium">
                    {formatCurrency(product.per_unit_cost, product.currency)}
                  </span>
                )}
              </div>

              {/* Digital Product Notice */}
              {isDigital && (
                <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/30 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-full">
                         <Check className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                    </div>
                    <span className="text-purple-800 dark:text-purple-300 text-sm font-bold">Instant digital download after payment</span>
                  </div>
                </div>
              )}

              {/* Stock Status - Enhanced for digital products */}
              <div className={`inline-flex items-center px-3 py-1 rounded-full font-bold text-xs uppercase tracking-wide ${
                isDigital
                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                  : listingType === 'Service' 
                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                    : availableStock > 10
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : availableStock > 0
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
              }`}>
                {isDigital 
                  ? 'Digital Download Available' 
                  : listingType === 'Service' 
                    ? 'Service Available' 
                    : availableStock > 10 ? 'In Stock' :
                      availableStock > 0 ? `Only ${availableStock} left` : 'Out of Stock'
                }
              </div>

              {/* Service-specific upfront payment info */}
              {listingType === 'Service' && product.upfront_payment_percentage > 0 && (
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/30 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                     <div className="p-2 bg-orange-100 dark:bg-orange-900/50 rounded-full">
                         <Check className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                    </div>
                    <span className="text-orange-800 dark:text-orange-300 text-sm font-bold">
                      {product.upfront_payment_percentage}% upfront payment required for booking
                    </span>
                  </div>
                </div>
              )}

              {/* Digital Product Download Info */}
              {isDigital && (
                <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                     <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-full mt-0.5">
                         <ShoppingBag className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <span className="text-blue-800 dark:text-blue-300 text-sm font-bold block mb-1">Download Information:</span>
                      <div className="text-blue-700 dark:text-blue-400 text-xs space-y-1 font-medium">
                        {product.allowed_downloads && <p>• {product.allowed_downloads} download attempts</p>}
                        {product.download_expiry_days && <p>• Valid for {product.download_expiry_days} days</p>}
                        {product.file_type && <p>• File type: {product.file_type.toUpperCase()}</p>}
                        {product.file_size && <p>• File size: {product.file_size}</p>}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Product Variations */}
              <ProductVariations />

              {/* Quantity Selector - Hidden for services and digital products */}
              <QuantitySelector />

              {/* Action Buttons */}
              <ActionButtons />

              {/* Trust Badges */}
              <TrustBadges />
            </div>
          </div>

          {/* Product Tabs */}
          <ProductTabs />

          {/* Tab Content */}
          <div className="mb-16">
            <TabContent />
          </div>

          {/* Related Products with Pagination */}
          <RelatedProducts />
        </div>
 {/* Sticky wrapper that stops at footer */}
        <StoreFrontFooter />
      </div>

      {/* Image Preview Modal */}
      <ImagePreviewModal />

      {/* Modals */}
      <ContactStoreModal
        show={showContactModal}
        onHide={() => setShowContactModal(false)}
        store={companyData}
        product={product}
      />

      <CartModal
        show={showCartModal}
        onHide={() => setShowCartModal(false)}
        cart={cart}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        clearCart={clearCartLocal}
        cartTotal={cartTotal}
        store={companyData}
        companyId={storeId}
      />

      <ShareStoreModal
        show={showShareStoreModal}
        onClose={() => setShowShareStoreModal(false)}
        link={shareLink}
        product={companyData}
      />

      {/* Service Booking Modal */}
      <ServiceBookingModal
        show={showServiceBookingModal}
        onHide={() => setShowServiceBookingModal(false)}
        service={product}
        store={companyData}
      />
    </>
  );
};

export default SharedProductPage;